import {
  isDate,
  amountToNumber,
} from './vue-global';
// Utility to get default month range for criteria (shared)
export function getDefaultMonthRange(today = new Date()) {
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-based
  let fromDateValue;
  let toDateValue;
  if (today.getDate() > 6) {
    // Use current month
    fromDateValue = `${month + 1}-01-${year}`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    toDateValue = `${month + 1}-${lastDay}-${year}`;
  } else {
    // Use previous month
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    fromDateValue = `${prevMonth + 1}-01-${prevYear}`;
    const lastDay = new Date(prevYear, prevMonth + 1, 0).getDate();
    toDateValue = `${prevMonth + 1}-${lastDay}-${prevYear}`;
  }
  return { fromDateValue, toDateValue };
}

// things that are probably useful across multiple programs
// (todo - subdivide this file it if gets longer than 100-400 lines of code?)

// As per business requirenmnt default value
export const defaultDurationOfCorrection = 2;

// Hover caption on the MTG and MISC "Correction" checkboxes. Shared so the two
// screens can't drift into two different wordings of the same rule.
export const correctionHoverCaption = 'A correction will not affect today\'s collections or deposits.  '
  + 'You will need to make manual GL entries to move money previously collected out of '
  + 'the collection fund and bank.';

// Has the user actually typed a payment row? The blank starter row that
// PaymentDetails auto-creates (`{ paymentType: '', amount: 0 }`) does not count,
// so ticking Correction on a fresh receipt doesn't need to prompt.
export const hasEnteredPaymentDetails = (paymentDetails) => (paymentDetails || []).some(
  (p) => !!(p || {}).paymentType || amountToNumber((p || {}).amount) !== 0,
);

// Shown before Correction clears rows the user already typed. Leaving them behind
// while the grid is disabled would save exactly the data corrections must not carry.
export const correctionClearsPaymentDetailsPrompt = 'A correction cannot carry payment details.'
  + ' This will remove the payment details you entered. Continue?';

// see warnings about ISO 8601 and RFC 2822 at:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date
// tries to prevent some of the most common ways that new Date(someStr) bites us with a non-UTC date
const tryReallyHardToStayUTC = (dateStr) => {
  const unknownLocaleDateStr = (dateStr || '');
  if (unknownLocaleDateStr.length > 0) {
    const formattedDateRegEx = /^([0-9]{1,4})[/\\-]{1}([0-9]{1,2})[/\\-]{1}([0-9]{1,4})$/;
    const match = formattedDateRegEx.exec(unknownLocaleDateStr);
    if (match) {
      if (match[1].length === 4) {
        const year = match[1].toString().padStart(4, '0');
        const month = match[2].toString().padStart(2, '0');
        const day = match[3].toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      // otherwise (match[3].length === 4)
      const year = match[3].toString().padStart(4, '0');
      const month = match[1].toString().padStart(2, '0');
      const day = match[2].toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }
  return unknownLocaleDateStr; // 🤷🏻‍♂️ don't break stuff
};

export const destructureDate = (date = new Date()) => {
  let dateObj = date;
  if (typeof dateObj === 'string') {
    const utcDateStr = tryReallyHardToStayUTC(dateObj);
    dateObj = new Date(utcDateStr);
  }
  return {
    year: dateObj.getUTCFullYear(),
    month: dateObj.getUTCMonth() + 1,
    day: dateObj.getUTCDate(),
  };
};

export const daysInMonth = (year, monthIndex) => (
  new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()
);

// Steps a penalty-date spinner by one full penalty period: moves to the next/
// previous month (direction = +1/-1) and lands on the last day of that county's
// penalty period (changeDay - 1), clamped to whatever the destination month
// actually has. When changeDay is 1, (changeDay - 1) is day 0, which JS's Date
// naturally resolves to the last day of the prior month - intentional, keeps
// stepping consistent from there instead of needing a special case.
export const stepPenaltyPeriodDate = (currentValue, direction, changeDay) => {
  const { year, month } = destructureDate(currentValue);
  const monthIndex = (month - 1) + direction;
  const targetDay = (changeDay || 1) - 1;
  const day = targetDay > 0 ? Math.min(targetDay, daysInMonth(year, monthIndex)) : targetDay;
  return destructureDate(new Date(Date.UTC(year, monthIndex, day)));
};

export const toTaxYear = (date = new Date()) => {
  const { year } = destructureDate(date);
  return year;
};

export const toIsoDate = (date = new Date()) => {
  const { year, month, day } = destructureDate(date);
  const monthStr = month.toString().padStart(2, '0');
  const dayStr = day.toString().padStart(2, '0');
  return `${year}-${monthStr}-${dayStr}`;
};

export const toShortDate = (date = new Date()) => {
  if (!date) return '';
  const { year, month, day } = destructureDate(date);
  const monthStr = month.toString().padStart(2, '0');
  const dayStr = day.toString().padStart(2, '0');
  return `${monthStr}-${dayStr}-${year}`;
};

export const dateDiff = (startDt, finishDt, config = {}) => {
  const start = isDate(startDt) ? startDt : new Date(startDt);
  const finish = isDate(finishDt) ? finishDt : new Date(finishDt);

  let diffMs;
  if (config.relative) {
    diffMs = finish - start; // milliseconds
  } else {
    diffMs = Math.abs(finish - start); // milliseconds
  }
  const mins = Math.floor(diffMs / 1000 / 60);
  const secs = Math.floor(diffMs / 1000) % 60;
  const years = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 365.25));
  return {
    diffMs,
    mins,
    secs,
    years,
  };
};

export const daysOffset = (days = 1, now = new Date()) => {
  const nowDt = new Date(now);
  return new Date(nowDt.valueOf() - 1000 * 3600 * days * 24);
};

export const dateDiffStr = (start, finish) => {
  const { mins, secs } = dateDiff(start, finish);
  return `${mins} minute(s), ${secs} second(s)`;
};

export const isNotBeforeDate = (value, todaysDate, offset) => {
  const registerDt = new Date(tryReallyHardToStayUTC(value));
  const twoDaysBefore = daysOffset(offset, new Date(todaysDate));
  const { diffMs } = dateDiff(twoDaysBefore, registerDt, { relative: 1 });
  return (diffMs > 0);
};

// Business days (Mon-Fri) between two dates -- no holiday calendar, matching
// the backend's isVoucherVoidWindowExpired (tmc-local-backend/src/util/od/
// voucherVoidWindow.js), which is the authoritative check. This mirror is UX
// only: it disables the Void control before the user bothers clicking it.
const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

export const businessDaysBetween = (from, to) => {
  const start = new Date(from);
  const end = new Date(to);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  let count = 0;
  const cursor = new Date(start);
  while (cursor < end) {
    cursor.setDate(cursor.getDate() + 1);
    if (!isWeekend(cursor)) count += 1;
  }
  return count;
};

export const VOUCHER_VOID_WINDOW_BUSINESS_DAYS = 2;

// True once more than VOUCHER_VOID_WINDOW_BUSINESS_DAYS business days have
// passed since registerDate. Registered Monday -> voidable through Wednesday,
// blocked from Thursday.
export const isVoucherVoidWindowExpired = (registerDate, now = new Date()) => {
  if (!registerDate) return false;
  const register = new Date(registerDate);
  if (Number.isNaN(register.getTime())) return false;
  return businessDaysBetween(register, now) > VOUCHER_VOID_WINDOW_BUSINESS_DAYS;
};

// A voucher is only age-locked once it has actually been registered with an
// amount. Not-yet-registered or zero-amount vouchers stay voidable with no
// time limit.
export const isVoucherAgeLocked = (voucher, now = new Date()) => Boolean(voucher)
  && voucher.type === 'VOUCHER'
  && Boolean(voucher.registration)
  && (voucher.amount || 0) > 0
  && isVoucherVoidWindowExpired(voucher.register, now);

export const toIsoDateStr = (date) => {
  if (!date) return '';
  const jsonDate = date.toJSON();
  return jsonDate ? jsonDate.slice(0, 10) : '';
};

export const toFiscalYear = (date) => {
  if (!date || Number.isNaN(date.getTime())) return null;
  const dateAsIsoStr = toIsoDateStr(date);
  const year = date.getUTCFullYear();
  const dateFyCutOffIsoStr = new Date(`${year}-07-01`).toJSON().slice(0, 10);
  if (dateAsIsoStr < dateFyCutOffIsoStr) {
    return year;
  }
  return year + 1;
};

// The calendar span of a fiscal year: 1 July of the prior year through 30 June (the inverse of
// toFiscalYear). Returned as ISO date strings, which is what the backend's Date scalar accepts.
// Used to scope a lookup whose key restarts each fiscal year -- e.g. a CC payment number.
export const toFiscalYearDateRange = (fiscalYear) => {
  const fy = parseInt(fiscalYear, 10);
  if (!fy) return null;
  return { from: `${fy - 1}-07-01`, to: `${fy}-06-30` };
};

// Mirrors the backend's posting-period guard (util/common.js) so bad business dates (e.g. a
// mistyped year) get caught client-side before the round-trip to the server.
export const FISCAL_YEAR_POSTING_GRACE = 1;

export const isFiscalYearWithinPostingPeriod = (fiscalYear, referenceDate = new Date()) => {
  const currentFiscalYear = toFiscalYear(referenceDate);
  if (!fiscalYear || !currentFiscalYear) return true;
  return fiscalYear >= currentFiscalYear - FISCAL_YEAR_POSTING_GRACE
    && fiscalYear <= currentFiscalYear + FISCAL_YEAR_POSTING_GRACE;
};

// get the fiscal years which cover these dates
export const getFiscalYears = (minDate, maxDate, allowAll = true) => {
  // coalesce invalid dates to "now" to minimize range returned
  const min = Number.isNaN(minDate.getTime()) ? new Date() : minDate;
  const max = Number.isNaN(maxDate.getTime()) ? new Date() : maxDate;
  const { year: minYear, month: minMonth } = destructureDate(min);
  const { year: maxYear, month: maxMonth } = destructureDate(max);
  // July 1 is the cut-off for a FY
  const maxFY = (maxMonth >= 7) ? maxYear + 1 : maxYear;
  const minFY = (minMonth < 7) ? minYear - 1 : minYear;

  let fy = minFY;
  const fyOptions = [];
  if (allowAll) {
    fyOptions.push({
      range: {},
      year: 0,
      name: '(all years)',
    });
  }
  while (fy < maxFY) {
    fyOptions.push({
      range: {
        from: `${fy}-07-01`,
        to: `${fy + 1}-06-30`,
      },
      year: (fy + 1),
      name: `FY ${fy}-${fy + 1}`,
    });
    fy += 1;
  }
  return fyOptions;
};

export const getFirstChar = (value) => {
  const firstChar = (value || '').substring(0, 1);
  return firstChar;
};

export const boolToStr = (value) => {
  let str = 'No';
  if (value !== undefined && value !== '') {
    str = value ? 'Yes' : 'No';
  }
  return str;
};

export const flagToYorEmpty = (value) => {
  let yOrEmpty = '';
  if (value !== undefined && value !== '' && value) {
    yOrEmpty = 'Y';
  }
  return yOrEmpty;
};

// GraphQL serializes TaxTypeEnum (see backend taxEnums.js) to its enum member NAME, not the
// lowercase mongo value - so a propertyTaxType field arrives here as 'R'/'P'/'B'/'M'/'S'/'U'/'X'
// (same convention already used for the existing taxType field, e.g. CommonActionButtons.vue's
// `taxType === 'R'` checks). 'X' (Unknown/unstamped) renders blank rather than 'Unknown', since
// that mirrors how other unset columns already look on these grids.
export const PROPERTY_TAX_TYPE_SHORT_CODES = {
  R: 'RE',
  P: 'PP',
  B: 'BP',
  M: 'MH',
  S: 'SA',
  U: 'UT',
};

export const propertyTaxTypeShortCode = (value) => PROPERTY_TAX_TYPE_SHORT_CODES[value] || '';

export const calculateFiscalYears = (value, allowAll = true, allYear = '(all years)') => {
  const yearsRange = [];
  if (Number.isInteger(value.min) && Number.isInteger(value.max)) {
    if (allowAll) {
      yearsRange.push({
        year: 0,
        name: allYear,
      });
    }
    for (let year = value.min; year <= value.max; year += 1) {
      yearsRange.push({
        year,
        name: `FY ${year - 1}-${year}`,
      });
    }
  }
  return yearsRange;
};
export const getFYList = (value) => calculateFiscalYears(value, false).map((x) => x.year);

export const getMaxFY = (fyArray) => {
  const sortedFYArray = (fyArray || []).sort((a, b) => b - a);
  return sortedFYArray[0] || null;
};

export const bankVoucherDataHeaders = [
  { text: 'FY', value: 'fiscalYear' },
  { text: 'Registration Date', value: 'register', formatter: 'shortDate' },
  { text: 'Register Number', value: 'registration' },
  { text: 'Voucher No', value: 'voucher' },
  { text: 'Amount', value: 'amount' },
  { text: 'Paid Date', value: 'date', formatter: 'shortDate' },
  { text: 'Department Name', value: 'deptName' },
  { text: 'Department No', value: 'deptNo' },
];

export const importFileProcessedData = {
  importBankVoucher: 'loadBankVoucherData',
};

export const usersLogoutTimePeriods = [
  { timePeriod: 600, description: '10 Minutes' },
  { timePeriod: 1200, description: '20 Minutes' },
  { timePeriod: 1800, description: '30 Minutes' },
  { timePeriod: 3600, description: '1 Hour' },
  { timePeriod: 7200, description: '2 Hour' },
  { timePeriod: 14400, description: '4 Hour' },
  { timePeriod: 28800, description: '8 Hour' },
  { timePeriod: 43200, description: '12 Hour' },
];

export const usersPassExpPeriods = [
  { expPeriod: 30, description: '1 Month' },
  { expPeriod: 60, description: '2 Months' },
  { expPeriod: 90, description: '3 Months' },
  { expPeriod: 120, description: '4 Months' },
  { expPeriod: 180, description: '6 Months' },
  { expPeriod: 365, description: '1 Year' },
];

/*
  Don't change the order of objects
  Because at some places we are using forteApiModes[0].apiURL as default
*/
export const forteApiModes = [
  {
    apiMode: 'SANDBOX',
    apiURL: 'https://sandbox.forte.net/checkout/v2/js',
  },
  {
    apiMode: 'PROD',
    apiURL: 'https://checkout.forte.net/v2/js',
  },
];

export const payrixApiModes = [
  {
    apiMode: 'SANDBOX',
    apiURL: 'https://test-api.payrix.com/txns',
  },
  {
    apiMode: 'PROD',
    apiURL: 'https://api.payrix.com/txns',
  },
];

export const checkDuplicate = ({
  stateArr, fieldName, val, isNewItem, isFundZero,
}) => {
  let stateData = stateArr.map((x) => x);
  if (!isNewItem) {
    stateData = stateData.filter((item) => item._id !== val._id);
  }
  if (isFundZero && val[fieldName] === '0000') {
    return true;
  }
  const found = stateData.some((el) => el[fieldName] === val[fieldName]);
  if (found) {
    return `${val[fieldName]} already exist`;
  }
  return true;
};

export const validateInputLength = ({
  fieldName, label = 'This field', val, requiredLength,
}) => {
  if (!!val[fieldName] && val[fieldName].length !== requiredLength) {
    return `${label} needs to be ${requiredLength} characters long`;
  }
  return true;
};

export const updateDateFormat = (criteria, dateForCriteria) => {
  const updatedCriteria = { ...criteria };
  dateForCriteria.forEach((element) => {
    if (updatedCriteria[element]) {
      updatedCriteria[element] = toIsoDate(updatedCriteria[element]);
    } else {
      updatedCriteria[element] = null;
    }
  });
  return updatedCriteria;
};

export const getLatestFy = (fiscalYears) => {
  if (!Array.isArray(fiscalYears) || (fiscalYears || []).length === 0) return 0;
  return (fiscalYears[fiscalYears.length - 1] || {}).year || 0;
};

export const getMonthYearRange = (inputYear) => {
  const range = [];
  for (let month = 12; month >= 1; month -= 1) {
    const year = month < 7 ? inputYear : inputYear - 1;
    /*
      to get month name :
      https://codingbeautydev.com/blog/javascript-convert-month-number-to-name/?expand_article=1
    */
    const date = new Date();
    date.setMonth(month - 1, 1);
    const monthName = date.toLocaleString('en-US', { month: 'short' });
    range.push({
      name: `${year} - ${monthName}`,
      monthyear: {
        month,
        year,
      },
    });
  }
  range.sort((a, b) => b.monthyear.year - a.monthyear.year);
  return range;
};

export const getIdFromList = (self, baseCrudKey) => {
  const items = self.items(baseCrudKey);
  const itemId = (items.length > 0) ? items[0]._id : null;
  return itemId;
};

export const isHealthDeptImports = (program) => program === 'Health Dept Warrant Imports';

export const isWorkDayImports = (program) => program === 'Work Day Imports';

export const reservedStatusCode = {
  min: 400,
  max: 499,
};

export const reservedCommentCode = {
  min: 1,
  max: 99,
};

export const createObjectFromArray = (records, keyName) => {
  const groupData = {};
  for (let j = 0; j < (records || []).length; j += 1) {
    const newObj = { ...records[j] };
    groupData[`${newObj[keyName]}`] = newObj;
  }
  return groupData;
};
// Format date as MM-DD-YYYY string
export const formatDateToString = (date) => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

export const getBusinessDateForPaymentEntry = () => localStorage.getItem('businessDateForPaymentEntry');

// Shown when Collect Info comes back as an error but the server gave no reason (e.g. the
// legacy tax/apportionment files were not imported).
export const noTaxCollectionsImportedAlert = 'No Tax Collections imported.  If this is incorrect, be sure to print your Cash Register Summary report and then Collect Info again';

// Alert text for a GL Collect Info result, or null when there is nothing to alert.
// A crash on the server carries its own message so support can tell it from a missing file.
export const collectInfoErrorAlert = (result) => {
  if (!result || result.status !== 'error') {
    return null;
  }
  if (result.errorMessage) {
    return `Collect Info failed: ${result.errorMessage}`;
  }
  return noTaxCollectionsImportedAlert;
};
